import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';

/* ════════════════════════════════════════════
   DATA
   ════════════════════════════════════════════ */

type QuizQuestion = {
  question: string;
  answers: string[];
  correctIndex: number;
  correctReaction: string;
  wrongReactions: string[];
};

const QUIZ: QuizQuestion[] = [
  {
    question: 'Gdzie była ta kawa, od której wszystko się zaczęło?',
    answers: ['Wrocław', 'Warszawa', 'Łódź', 'Kraków'],
    correctIndex: 2,
    correctReaction:
      'Tak. Łódź.\nI ja, który udawał, że nie patrzy… a w środku już wiedział.\nDuma: +10',
    wrongReactions: [
      '🥺 Wrocław? Piękne miasto, ale nasza historia zaczęła się gdzie indziej… przy jednej kawie, która zmieniła wszystko.',
      '🥺 Warszawa? Nie tym razem… ale ta jedna kawa w innym mieście zmieniła mi życie bardziej niż jakiekolwiek miejsce.',
      '',
      '🥺 Kraków? Romantycznie, ale to nie tam udawałem, że nie patrzę na Ciebie…',
    ],
  },
  {
    question: 'Co najbardziej mnie zaskoczyło w naszej relacji?',
    answers: [
      'Jak dobrze gotujesz',
      'Jak bardzo lubię Ci wyznawać uczucia',
      'Jak szybko minął czas',
      'Jak często się śmiejemy',
    ],
    correctIndex: 1,
    correctReaction:
      'Dokładnie to.\nNigdy wcześniej nie ufałem komuś tak… bez hamulców.\nI przy Tobie to jest przyjemne. Bezpieczne. Prawdziwe.',
    wrongReactions: [
      '😔 Gotowanie? Przypominam: ja i tak jestem ugotowany… ale zaskoczyło mnie coś głębszego.',
      '',
      '😔 Czas leci, to fakt… ale bardziej zaskoczyło mnie to, jak bardzo otworzyłem się przy Tobie. Bez hamulców.',
      '😔 Śmiejemy się dużo, to prawda… ale mnie najbardziej zaskoczyło, że przy Tobie chcę mówić o uczuciach. I to głośno.',
    ],
  },
  {
    question: 'Co było pierwszą rzeczą, którą miałaś wpisać do dzienniczka wdzięczności?',
    answers: ['Obraz', 'Świeża pościel', 'Ty 🩷', 'Spokój'],
    correctIndex: 2,
    correctReaction:
      '„Ty".\nI w tym jednym słowie było wszystko.\nWtedy mi serce zmiękło.',
    wrongReactions: [
      '🫣 Obraz? Ładna odpowiedź… ale to jedno krótkie słowo było dla mnie ważniejsze niż jakikolwiek obraz.',
      '🫣 Świeża pościel ma moc, nie kłócę się… ale tam było coś jeszcze krótszego i mocniejszego.',
      '',
      '🫣 Spokój to piękna rzecz… ale tam było jedno słowo, które ścisnęło mi serce bardziej.',
    ],
  },
  {
    question: 'Jednym słowem: co było dla Ciebie najbardziej zaskakujące w naszej relacji?',
    answers: ['Zrozumienie', 'Tempo', 'Przypadek', 'Zazdrość'],
    correctIndex: 0,
    correctReaction:
      'Zrozumienie.\nTo słowo jest ciche, ale ma największą wagę.\nI jeśli mam być szczery… jestem z Ciebie dumny, że potrafisz je wybierać.',
    wrongReactions: [
      '',
      '😶 Tempo? Szybko się dzieje, to fakt… ale to inne słowo niesie największą wagę w tym, co budujemy.',
      '😶 Przypadek? Nie wierzę w przypadki, kiedy chodzi o Nas… i to inne słowo trzyma nas razem.',
      '😶 Zazdrość? Mam nadzieję, że nie… bo to coś zupełnie innego jest fundamentem tego, co mamy.',
    ],
  },
  {
    question: "Czego tak naprawdę bałaś się najbardziej, kiedy już byłaś 'odkryta'?",
    answers: [
      'Że się znudzę',
      'Że stracisz kontrolę nad uczuciami',
      'Że będzie bolało, jeśli coś nie wyjdzie',
      'Wszystkiego naraz',
    ],
    correctIndex: 3,
    correctReaction:
      'Tak… to ten sam strach, tylko w różnych ubraniach.\nI właśnie dlatego jestem z Ciebie dumny, że mimo tego potrafisz być prawdziwa.',
    wrongReactions: [
      '🥺 Że się znudzę? Nigdy… ale myślę, że ten strach był szerszy. Obejmował wszystko naraz.',
      '🥺 Kontrola nad uczuciami… to na pewno część tego. Ale myślę, że to było jeszcze więcej — wszystko na raz.',
      '🥺 Ból to realne ryzyko… ale myślę, że ten strach miał więcej twarzy niż jedną.',
      '',
    ],
  },
  {
    question: 'Jak brzmiało to zdanie, które uderzyło mnie najmocniej?',
    answers: [
      'Nie będzie nudno 😈',
      'Chciałabym zostać na zawsze tam, gdzie całą siebie obnażę',
      'Nie wiem',
      'Uparty jesteś',
    ],
    correctIndex: 1,
    correctReaction:
      'Tak. To zdanie.\nI ja je noszę w sobie.\nBo ono nie jest o słowach. Ono jest o odwadze.',
    wrongReactions: [
      '😔 „Nie będzie nudno" — to mnie rozśmieszyło, ale inne zdanie uderzyło mnie prosto w serce. Głębiej.',
      '',
      '😔 „Nie wiem" to uczciwa odpowiedź… ale było jedno zdanie, które noszę w sobie do dziś.',
      '😔 Uparty jestem, fakt… ale jedno inne zdanie zostało we mnie na zawsze. O odwadze i odsłanianiu się.',
    ],
  },
  {
    question: 'Co było kluczem w naszej rozmowie o presji i decyzjach?',
    answers: ['Kiedy', 'Razem', 'Natychmiast', 'Nie rozmawiajmy'],
    correctIndex: 1,
    correctReaction:
      `Dokładnie. „Razem".\nBo Ty potrzebujesz poczucia, że nie jesteś sama.\nA ja uczę się, że „razem" jest ważniejsze niż „kiedy".`,
    wrongReactions: [
      `🥲 „Kiedy"? To ważne pytanie… ale kluczem było coś cieplejszego. Słowo, które znaczy: nie jesteś sama.`,
      '',
      `🥲 „Natychmiast"? Nie, nigdy nie chodzi o pośpiech… kluczem było poczucie, że idziemy w tym wspólnie.`,
      `🥲 „Nie rozmawiajmy"? Wręcz przeciwnie… kluczem było jedno słowo, które znaczy: robimy to wspólnie.`,
    ],
  },
  {
    question: 'Jaka jest moja ulubiona gra, bo prowadzi do najprawdziwszych rozmów?',
    answers: ['FIFA', 'Porozmawiajmy', 'Scrabble', 'Monopoly'],
    correctIndex: 1,
    correctReaction:
      'Porozmawiajmy.\nBo tam patrzę Ci w oczy i czuję, że jesteśmy drużyną.',
    wrongReactions: [
      '😏 FIFA? Blondasku… piłka jest fajna, ale żadna gra nie daje mi tyle co patrzenie Ci w oczy przy prawdziwej rozmowie.',
      '',
      '😏 Scrabble? Lubię słowa, ale wolę te wypowiedziane prosto do Ciebie, nie ułożone na planszy.',
      '😏 Monopoly? Ja bym Ci oddał wszystko bez licytacji… ale jest jedna gra, przy której czuję, że jesteśmy drużyną.',
    ],
  },
  {
    question: 'Co pojawiło mi się w głowie, kiedy trzymałem małe bobo za palec?',
    answers: [
      'Chcę uciec',
      `„Chcę być ojcem" i że to mogłoby być nasze`,
      'To za wcześnie',
      'Jestem zmęczony',
    ],
    correctIndex: 1,
    correctReaction:
      'Tak… to było tak dziwnie prawdziwe.\nI to uczucie zostało we mnie na długo.',
    wrongReactions: [
      '🥺 Uciec? Nigdy… wręcz przeciwnie. Ten mały paluszek sprawił, że chciałem zostać. I zobaczyłem Nas.',
      '',
      '🥺 Za wcześnie? Może racjonalnie tak… ale serce nie czeka na rozkład jazdy. Ono po prostu wiedziało.',
      '🥺 Zmęczony? Może i tak, ale ta myśl o Nas była silniejsza niż jakiekolwiek zmęczenie.',
    ],
  },
  {
    question: 'Co jest dla mnie w relacji najważniejsze?',
    answers: ['Plan i kontrola', 'Perfekcja', 'Bliskość, obecność i czas razem', 'Spokój bez rozmów'],
    correctIndex: 2,
    correctReaction:
      'Tak. Bliskość. Czas. Obecność.\nI Ty.\nBo przy Tobie wszystko inne robi się mniej ważne.',
    wrongReactions: [
      '🤭 Plan i kontrola? Jutro wprowadzam sprinty relacyjne… a tak serio, chodzi o coś cieplejszego. O bycie razem.',
      '🤭 Perfekcja? Nie szukam ideału… szukam Ciebie. I tego, żebyśmy mieli czas. Razem. Blisko.',
      '',
      '🤭 Spokój bez rozmów? Z Tobą chcę rozmawiać do rana… bo najważniejsza jest bliskość i obecność.',
    ],
  },
];

const RANDOM_WRONG_VALIDATIONS = [
  '🥺 Myślałem, że to oczywiste… Ale nie zmienia to faktu, że kocham Cię tak samo mocno!',
  "😏 To nie tak… chyba mój mózg włączył tryb: 'zakochany' i pomylił fakty.",
  "🫣 Ała. Moje serce właśnie zrobiło małe 'pik' ze smutku.",
  '🤭 No dobrze… ale pamiętaj, że i tak jesteś najlepszą odpowiedzią.',
  '😶 Ja byłem pewien… ale najwyraźniej muszę przejść szkolenie u Blondaska.',
  '🥲 Nie zgadza mi się, ale niech to będzie Nasza wspólna decyzja.',
  '😔 Serio? To ja już przygotowałem wzruszenie do innej odpowiedzi…',
  '😈 Blondasku… ja to zapamiętam. (żartuję… chyba)',
  '🥺 W mojej głowie to wyglądało inaczej, ale Ty i tak jesteś moją prawdą.',
  '🤍 Zła odpowiedź, ale dobra wiadomość: nadal Cię kocham.',
  '🫣 Uwaga, mój romantyzm wykrył błąd w systemie.',
  '😏 Ja myślałem, że wiem wszystko… ale to dobrze, bo mogę zakochiwać się w Tobie jeszcze raz.',
];

const ACCESS_WRONG_MESSAGES = [
  '🥺 To nie ta data… A ja byłem pewien, że pamiętasz dzień, w którym przepadłem.',
  '😏 Prawie. Ale ta jedna kawa zmieniła mi życie trochę bardziej.',
  '🫣 Nie no… to wtedy udawałem, że nie patrzę na Ciebie. Nie pomyl tego, proszę.',
  '🤭 Moje serce mówi: błąd. Blondasek mówi: spróbuj jeszcze raz.',
];

type MemoryTile = {
  title: string;
  content: string;
};

const MEMORY_TILES: MemoryTile[] = [
  {
    title: 'Ta jedna kawa',
    content:
      'Od tej kawy wszystko zaczęło mieć sens.\nI do dziś pamiętam, jak patrzyłem na Ciebie tak, żebyś nie wyczytała, że już wtedy… przepadłem.',
  },
  {
    title: 'Zbroja',
    content:
      'Widziałem Twoje drzwi zamknięte na cztery spusty.\nI jestem z Ciebie dumny, że nie musisz już trzymać klucza w dłoni cały czas.',
  },
  {
    title: 'Zrozumienie',
    content:
      'Powiedziałaś jedno słowo: zrozumienie.\nI ja wtedy poczułem, że to jest fundament.\nNie krzyk. Nie fajerwerk. Tylko bezpieczeństwo.',
  },
  {
    title: 'Czyny i czas',
    content:
      'Powiedziałaś mi, że umiem przekonać Cię czynami.\nI że to zmienia się w czasie.\nI ja chcę być dokładnie taki — konsekwentny, nie na pokaz.',
  },
  {
    title: 'Dzienniczek',
    content:
      `„Ty" w dzienniczku wdzięczności.\nW jednym słowie zamknęłaś cały mój sens.`,
  },
  {
    title: 'Nie będzie nudno 😈',
    content:
      'Powiedziałaś to i ja się uśmiechnąłem do ekranu jak głupi.\nBo z Tobą… naprawdę nie będzie nudno. I ja tego chcę.',
  },
  {
    title: 'Bobo',
    content:
      'Ten mały paluszek i moja myśl, że to mogłoby być nasze.\nNie dlatego, że planuję.\nTylko dlatego, że Ty we mnie budzisz takie marzenia.',
  },
  {
    title: 'Dom',
    content:
      `Kiedy mówię „dom", to nie mówię o miejscu.\nMówię o Tobie.`,
  },
];

const LOVE_LETTER = `Blondasku…

Walentynki to niby tylko data w kalendarzu. Jedno z tych świąt, które ludzie obchodzą, bo tak wypada, a ja dzisiaj czuję, że to po prostu pretekst, żeby powiedzieć Ci coś, co i tak mówię codziennie, ale nigdy mi się nie znudzi.

Kocham Cię, jesteś dla mnie całym światem.

Kocham Cię tak, że czasem aż mnie to boli w klatce piersiowej, kiedy Cię przy mnie nie ma. Codziennie budzę się i pierwsza myśl jest o Tobie. Wieczorem zasypiam, przytulając poduszkę wzdłuż i wyobrażając sobie, że to Twoje ciało.

Przy Tobie nauczyłem się czegoś, czego nie spodziewałem się w sobie znaleźć. Delikatności. Cierpliwości. Uważności. Nauczyłem się być w relacji i naprawdę nad nią pracować. Bez potrzeby udowadniania czegokolwiek. Bez lęku, że moje uczucia są „za duże". Bo Ty potrafisz je przyjąć. Nie uciekasz. Nie wyśmiewasz. Nie umniejszasz. Ty je trzymasz i przyjmujesz do swojego serduszka.

I wiesz co? To jest najseksowniejsza rzecz na świecie.

To, że potrafisz być odkryta. Że mówisz „kocham Cię" i nie chowasz tego pod żartem. Że potrafisz powiedzieć, że się boisz. Że boisz się stracić, boisz się, że zaboli, boisz się oddać kontrolę… a mimo to oddajesz mi kawałek siebie. To jest odwaga i jestem z Ciebie dumny. Tak bardzo dumny…

Czasem, kiedy myślę o Tobie, nie myślę tylko o rozmowach i planach. Myślę o tym, jak byś wyglądała, gdybym mógł Cię teraz przyciągnąć do siebie. Jak wsunąłbym dłoń w Twoje włosy. Jak pocałowałbym Cię powoli, bez pośpiechu, jakbym miał przed sobą cały świat czasu. Jak usiadłabyś na blacie w kuchni i spojrzała na mnie tym wzrokiem, który mówi więcej niż słowa.

Chcę Cię.
Nie tylko jako dziewczynę. Nie tylko jako przyszłość.
Chcę Cię jako kobietę. Moją kobietę.

Ale jeszcze bardziej niż Twojego ciała, chcę Twojej głowy. Twojego śmiechu. Twojej wrażliwości. Tego momentu, kiedy mówisz coś cicho i myślisz, że to nie jest ważne, a dla mnie to jest wszystko.

Walentynki to święto miłości.
A ja mam szczęście kochać kogoś, kto jest jednocześnie moją przyjaciółką, moim spokojem, moim wyzwaniem i moim domem.

Czasem wyobrażam sobie, jak wracasz zmęczona do mieszkania. Jak zdejmujesz buty, odkładasz plecak, a ja stoję tam i po prostu patrzę na Ciebie z tą myślą: „To jest mój ulubiony człowiek." Podchodzę, obejmuję Cię od tyłu, całuję w szyję i mówię: „Wiesz, że jesteś najpiękniejszą i najlepszą rzeczą, jaka mnie spotkała?"

Bo jesteś.

I jeśli mam być szczery — nie potrzebuję czerwonych serduszek, kolacji przy świecach ani prezentów. Wystarczy mi to, że jesteś. Że mogę do Ciebie napisać. Że mogę usłyszeć Twój głos. Że mogę mówić Ci, że Cię kocham i nie czuć wstydu ani blokady.

Chcę z Tobą przeżyć jeszcze wiele Walentynek.
Tych spokojnych.
Tych pełnych śmiechu.
Tych z nutą zazdrości i ognia.
Tych, w których będziemy się kłócić o głupoty, a potem godzić w sposób, który przypomina nam, że należymy do siebie.

Blondasku…
Jeśli kiedykolwiek zwątpisz w to, czy jesteś wystarczająca — przypomnij sobie ten list. Przypomnij sobie, że jest ktoś, kto patrzy na Ciebie i widzi kobietę, z którą chce budzić się każdego dnia. Który jest z Ciebie dumny. Który Ci ufa. Który Cię pragnie.

I który kocha Cię tak, że nawet zwykłe „kocham Cię" wydaje się za małe.

Na zawsze Twój
Arek 🩷`;

/* ════════════════════════════════════════════
   SHARED UI PRIMITIVES
   ════════════════════════════════════════════ */

const FloatingHeart = ({
  delay,
  size,
  left,
  dark,
}: {
  delay: number;
  size: number;
  left: number;
  dark: boolean;
}): React.JSX.Element => (
  <div
    className="absolute pointer-events-none animate-v-float"
    style={{
      left: `${left}%`,
      animationDelay: `${delay}s`,
      fontSize: `${size}rem`,
      color: dark ? 'rgba(251, 113, 133, 0.15)' : 'rgba(244, 114, 182, 0.2)',
    }}
  >
    &#10084;
  </div>
);

const generateHearts = (
  n: number
): Array<{ id: number; delay: number; size: number; left: number }> =>
  Array.from({ length: n }, (_, i) => ({
    id: i,
    delay: Math.random() * 10,
    size: Math.random() * 1.6 + 0.6,
    left: Math.random() * 100,
  }));

/* ── progress bar (heart) ── */
const HeartProgress = ({
  percent,
  dark,
}: {
  percent: number;
  dark: boolean;
}): React.JSX.Element => (
  <div className="flex items-center gap-2 text-xs select-none">
    <span>❤️</span>
    <div
      className={`w-24 h-1.5 rounded-full overflow-hidden ${dark ? 'bg-white/10' : 'bg-rose-200/60'}`}
    >
      <div
        className="h-full rounded-full transition-all duration-700 bg-gradient-to-r from-rose-400 to-pink-500"
        style={{ width: `${percent}%` }}
      />
    </div>
    <span className={dark ? 'text-rose-300/60' : 'text-rose-400/60'}>{percent}%</span>
  </div>
);

/* ── top bar ── */
const TopBar = ({
  dark,
  onToggleDark,
  heartPercent,
  statusText,
}: {
  dark: boolean;
  onToggleDark: () => void;
  heartPercent: number;
  statusText: string;
}): React.JSX.Element => (
  <div className="fixed top-0 inset-x-0 z-50 flex items-center justify-between px-4 py-2.5">
    <HeartProgress percent={heartPercent} dark={dark} />

    <button
      onClick={onToggleDark}
      className={`text-lg transition-transform hover:scale-110 ${dark ? 'text-yellow-300' : 'text-indigo-400'}`}
      aria-label="Toggle dark mode"
    >
      {dark ? '☀️' : '🌙'}
    </button>

    <span
      className={`text-[10px] font-light tracking-wide ${dark ? 'text-rose-300/40' : 'text-rose-400/40'}`}
    >
      {statusText}
    </span>
  </div>
);

/* ── card wrapper ── */
const CardShell = ({
  children,
  dark,
  wide,
}: {
  children: React.ReactNode;
  dark: boolean;
  wide?: boolean;
}): React.JSX.Element => {
  const hearts = useMemo(() => generateHearts(14), []);
  return (
    <div
      className={`fixed inset-0 flex items-center justify-center overflow-hidden transition-colors duration-500 ${
        dark
          ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950'
          : 'bg-gradient-to-br from-rose-100 via-pink-50 to-amber-50'
      }`}
    >
      {hearts.map((h) => (
        <FloatingHeart key={h.id} delay={h.delay} size={h.size} left={h.left} dark={dark} />
      ))}
      <div
        className={`z-10 mx-4 ${wide ? 'w-full max-w-2xl' : 'w-full max-w-lg'} max-h-[85vh] overflow-y-auto rounded-3xl shadow-xl p-6 md:p-10 transition-colors duration-500 animate-v-fadeIn ${
          dark ? 'bg-gray-900/80 backdrop-blur-md' : 'bg-white/80 backdrop-blur-md'
        }`}
      >
        {children}
      </div>
    </div>
  );
};

/* ── primary button ── */
const PrimaryButton = ({
  children,
  onClick,
  dark,
}: {
  children: React.ReactNode;
  onClick: () => void;
  dark: boolean;
}): React.JSX.Element => (
  <button
    onClick={onClick}
    className={`mt-6 px-8 py-3 rounded-full text-base font-light shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${
      dark
        ? 'bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-500 hover:to-pink-500 text-white'
        : 'bg-gradient-to-r from-rose-400 to-pink-400 hover:from-rose-500 hover:to-pink-500 text-white'
    }`}
  >
    {children}
  </button>
);

/* ════════════════════════════════════════════
   CARD 1 — ACCESS GATE
   ════════════════════════════════════════════ */

const CORRECT_DATES = ['09.02.2025', '2025-02-09', '9.02.2025', '9.2.2025', '09.2.2025'];

const CardAccess = ({
  dark,
  onNext,
}: {
  dark: boolean;
  onNext: () => void;
}): React.JSX.Element => {
  const [input, setInput] = useState('');
  const [shake, setShake] = useState(false);
  const [error, setError] = useState('');
  const [verified, setVerified] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (CORRECT_DATES.includes(input.trim())) {
        setVerified(true);
        setError('');
      } else {
        setShake(true);
        setError(ACCESS_WRONG_MESSAGES[attempts % ACCESS_WRONG_MESSAGES.length]);
        setAttempts((p) => p + 1);
        setTimeout(() => setShake(false), 600);
      }
    },
    [input, attempts]
  );

  if (verified) {
    return (
      <CardShell dark={dark}>
        <div className="text-center animate-v-fadeIn">
          <div className="text-5xl mb-5">💌</div>
          <h2
            className={`text-2xl md:text-3xl font-light mb-3 leading-relaxed ${dark ? 'text-rose-200' : 'text-rose-800'}`}
          >
            Dostęp przyznany.
          </h2>
          <p className={`text-lg font-light italic ${dark ? 'text-rose-300/80' : 'text-rose-600'}`}>
            Właścicielka mojego serca potwierdzona &#10003;
          </p>
          <PrimaryButton onClick={onNext} dark={dark}>
            Wejdź, Blondasku 🫶
          </PrimaryButton>
        </div>
      </CardShell>
    );
  }

  return (
    <CardShell dark={dark}>
      <div className="text-center">
        <div className="text-5xl mb-5">🔒</div>
        <p className={`text-sm mb-4 italic ${dark ? 'text-rose-300/60' : 'text-rose-400/80'}`}>
          Skoro kliknęłaś TAK i jesteś moją prawdziwą walentynką… potwierdź, że to naprawdę Ty.
        </p>
        <h1
          className={`text-xl md:text-2xl font-light mb-2 leading-relaxed ${dark ? 'text-rose-100' : 'text-gray-700'}`}
        >
          Dostęp do mojego serca
        </h1>
        <p className={`text-base mb-2 font-light ${dark ? 'text-rose-200/70' : 'text-gray-500'}`}>
          Ta strona jest tylko dla jednej osoby.
        </p>
        <p className={`text-base mb-1 font-light ${dark ? 'text-rose-200/70' : 'text-gray-500'}`}>
          Jeśli tu jesteś, to znaczy, że jesteś moim Blondaskiem.
        </p>
        <p
          className={`text-base mb-6 font-light ${dark ? 'text-rose-200/70' : 'text-gray-500'}`}
        >
          A do mojego serca nie wchodzi się przypadkiem.
        </p>

        <p className={`text-xs mb-4 ${dark ? 'text-rose-300/40' : 'text-gray-400'}`}>
          Wpisz datę naszej pierwszej kawy (DD.MM.RRRR).
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className={`transition-transform ${shake ? 'animate-v-shake' : ''}`}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="__ . __ . ____"
              className={`w-full px-6 py-4 rounded-2xl border-2 focus:outline-none text-center text-lg font-light transition-colors ${
                dark
                  ? 'bg-gray-800/60 border-rose-800 focus:border-rose-500 text-rose-100 placeholder:text-gray-600'
                  : 'bg-white/70 border-rose-200 focus:border-rose-400 text-gray-700 placeholder:text-gray-400'
              }`}
            />
          </div>

          {error && (
            <p
              className={`text-sm font-light leading-relaxed whitespace-pre-line animate-v-fadeIn ${dark ? 'text-rose-300' : 'text-rose-500'}`}
            >
              {error}
            </p>
          )}

          <PrimaryButton
            onClick={() => handleSubmit({ preventDefault: () => undefined } as React.FormEvent)}
            dark={dark}
          >
            Otwórz
          </PrimaryButton>
        </form>
      </div>
    </CardShell>
  );
};

/* ════════════════════════════════════════════
   CARD 2 — INTRO
   ════════════════════════════════════════════ */

const CardIntro = ({
  dark,
  onNext,
}: {
  dark: boolean;
  onNext: () => void;
}): React.JSX.Element => (
  <CardShell dark={dark}>
    <div className="text-center">
      <div className="text-4xl mb-5">✨</div>
      <h1
        className={`text-2xl md:text-3xl font-light mb-6 leading-relaxed ${dark ? 'text-rose-100' : 'text-gray-800'}`}
      >
        Rok temu… zaczęło się wszystko.
      </h1>
      <div
        className={`text-base font-light leading-relaxed space-y-3 ${dark ? 'text-rose-200/80' : 'text-gray-600'}`}
      >
        <p>Jedna kawa. Jedno spojrzenie.</p>
        <p>
          I to uczucie, którego nie dało się już
          {' '}
          &ldquo;odłożyć na później&rdquo;.
        </p>
        <p className="pt-2">
          Dzisiaj przejdziesz przez 10 krótkich misji.
          <br />
          To nie test wiedzy… tylko pretekst, żebym mógł Ci powiedzieć:
        </p>
        <p className={`italic ${dark ? 'text-rose-300' : 'text-rose-600'}`}>
          jestem z Ciebie dumny.
        </p>
      </div>
      <PrimaryButton onClick={onNext} dark={dark}>
        Start misji 1/10
      </PrimaryButton>
    </div>
  </CardShell>
);

/* ════════════════════════════════════════════
   CARDS 3–12 — QUIZ
   ════════════════════════════════════════════ */

const ProgressDots = ({
  total,
  current,
  dark,
}: {
  total: number;
  current: number;
  dark: boolean;
}): React.JSX.Element => (
  <div className="flex gap-2 justify-center mb-6">
    {Array.from({ length: total }, (_, i) => (
      <div
        key={i}
        className={`w-2 h-2 rounded-full transition-all duration-500 ${
          i < current
            ? dark
              ? 'bg-rose-400 scale-110'
              : 'bg-rose-500 scale-110'
            : i === current
              ? 'bg-rose-400 scale-125 animate-pulse'
              : dark
                ? 'bg-gray-700'
                : 'bg-rose-200'
        }`}
      />
    ))}
  </div>
);

const CardQuiz = ({
  dark,
  onComplete,
  onProgress,
}: {
  dark: boolean;
  onComplete: (score: number) => void;
  onProgress: (fraction: number) => void;
}): React.JSX.Element => {
  const [qi, setQi] = useState(0);
  const [score, setScore] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [transitioning, setTransitioning] = useState(false);
  const usedValidations = useRef<Set<number>>(new Set());

  const q = QUIZ[qi];

  const pickRandomValidation = useCallback((): string => {
    const available = RANDOM_WRONG_VALIDATIONS.map((_, i) => i).filter(
      (i) => !usedValidations.current.has(i)
    );
    if (available.length === 0) {
      usedValidations.current.clear();
      return RANDOM_WRONG_VALIDATIONS[Math.floor(Math.random() * RANDOM_WRONG_VALIDATIONS.length)];
    }
    const pick = available[Math.floor(Math.random() * available.length)];
    usedValidations.current.add(pick);
    return RANDOM_WRONG_VALIDATIONS[pick];
  }, []);

  const isCorrect = selected === q.correctIndex;

  const handleAnswer = useCallback(
    (idx: number) => {
      if (selected !== null) return;
      setSelected(idx);
      if (idx === q.correctIndex) setScore((s) => s + 1);
    },
    [selected, q.correctIndex]
  );

  const handleNext = useCallback(() => {
    setTransitioning(true);
    setTimeout(() => {
      if (qi < QUIZ.length - 1) {
        const nextQ = qi + 1;
        setQi(nextQ);
        setSelected(null);
        setTransitioning(false);
        onProgress((nextQ + 1) / QUIZ.length);
      } else {
        onComplete(score);
      }
    }, 400);
  }, [qi, onComplete, onProgress, score]);

  const wrongText = useMemo(() => {
    if (selected === null || isCorrect) return '';
    const perAnswer = q.wrongReactions[selected];
    return perAnswer || pickRandomValidation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, isCorrect]);

  return (
    <CardShell dark={dark}>
      <div
        className={`transition-all duration-400 ${transitioning ? 'opacity-0 translate-y-3' : 'opacity-100 translate-y-0'}`}
      >
        <ProgressDots total={QUIZ.length} current={qi} dark={dark} />

        <p className={`text-sm mb-4 font-light ${dark ? 'text-rose-300/60' : 'text-rose-400'}`}>
          Misja {qi + 1}/{QUIZ.length}
        </p>

        <h2
          className={`text-lg md:text-xl font-light mb-7 leading-relaxed ${dark ? 'text-rose-100' : 'text-gray-800'}`}
        >
          {q.question}
        </h2>

        <div className="space-y-3">
          {q.answers.map((a, idx) => {
            let cls =
              'w-full text-left px-5 py-3.5 rounded-2xl border-2 transition-all duration-300 font-light text-sm md:text-base ';

            if (selected === null) {
              cls += dark
                ? 'border-gray-700 hover:border-rose-500 hover:bg-rose-950/30 text-rose-100 cursor-pointer'
                : 'border-rose-100 hover:border-rose-300 hover:bg-rose-50 text-gray-700 cursor-pointer';
            } else if (idx === q.correctIndex) {
              cls += dark
                ? 'border-emerald-500 bg-emerald-950/40 text-emerald-300'
                : 'border-emerald-400 bg-emerald-50 text-emerald-800';
            } else if (idx === selected && !isCorrect) {
              cls += dark
                ? 'border-rose-500 bg-rose-950/40 text-rose-300 animate-v-shake'
                : 'border-rose-400 bg-rose-50 text-rose-700 animate-v-shake';
            } else {
              cls += dark ? 'border-gray-800 text-gray-600 opacity-50' : 'border-gray-100 text-gray-400 opacity-50';
            }

            return (
              <button key={idx} onClick={() => handleAnswer(idx)} disabled={selected !== null} className={cls}>
                {a}
              </button>
            );
          })}
        </div>

        {selected !== null && (
          <div className="mt-5 animate-v-fadeIn">
            <div
              className={`rounded-2xl p-4 ${
                isCorrect
                  ? dark
                    ? 'bg-emerald-950/30 border border-emerald-700'
                    : 'bg-emerald-50 border border-emerald-200'
                  : dark
                    ? 'bg-rose-950/30 border border-rose-700'
                    : 'bg-rose-50 border border-rose-200'
              }`}
            >
              <div className="flex items-start gap-3">
                {isCorrect && <span className="text-xl flex-shrink-0 mt-0.5">❤️</span>}
                <p
                  className={`text-sm font-light leading-relaxed whitespace-pre-line ${
                    isCorrect
                      ? dark
                        ? 'text-emerald-300'
                        : 'text-emerald-700'
                      : dark
                        ? 'text-rose-300'
                        : 'text-rose-600'
                  }`}
                >
                  {isCorrect ? q.correctReaction : wrongText}
                </p>
              </div>
            </div>

            <div className="text-center">
              {isCorrect ? (
                <PrimaryButton onClick={handleNext} dark={dark}>
                  {qi < QUIZ.length - 1 ? 'Dalej' : 'Zobacz finał'}
                </PrimaryButton>
              ) : (
                <button
                  onClick={() => setSelected(null)}
                  className={`mt-5 px-6 py-2.5 rounded-full text-sm font-light transition-all duration-300 hover:scale-105 ${
                    dark
                      ? 'bg-gray-800 hover:bg-gray-700 text-rose-200'
                      : 'bg-rose-100 hover:bg-rose-200 text-rose-600'
                  }`}
                >
                  Ok, dobra… poprawiam 😅
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </CardShell>
  );
};

/* ════════════════════════════════════════════
   CARD 13 — SUMMARY
   ════════════════════════════════════════════ */

const CardSummary = ({
  dark,
  onNext,
}: {
  dark: boolean;
  onNext: () => void;
}): React.JSX.Element => (
  <CardShell dark={dark}>
    <div className="text-center">
      <div className="text-4xl mb-5">🏆</div>
      <h1
        className={`text-2xl md:text-3xl font-light mb-5 ${dark ? 'text-rose-100' : 'text-gray-800'}`}
      >
        Wynik? Nieważne.
      </h1>
      <p className={`text-base font-light mb-3 ${dark ? 'text-rose-200/80' : 'text-gray-600'}`}>
        To nie jest test. To jest pretekst.
      </p>
      <p className={`text-base font-light mb-6 ${dark ? 'text-rose-200/80' : 'text-gray-600'}`}>
        Bo i tak jesteś jedyną odpowiedzią, która mi pasuje.
      </p>
      <p className={`text-lg italic ${dark ? 'text-rose-300' : 'text-rose-600'}`}>
        Blondasek… jestem z Ciebie dumny. Naprawdę.
      </p>
      <PrimaryButton onClick={onNext} dark={dark}>
        Otwórz prezent
      </PrimaryButton>
    </div>
  </CardShell>
);

/* ════════════════════════════════════════════
   CARD 14 — GALLERY (memory tiles)
   ════════════════════════════════════════════ */

const CardGallery = ({
  dark,
  onNext,
}: {
  dark: boolean;
  onNext: () => void;
}): React.JSX.Element => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <CardShell dark={dark} wide>
      <div className="text-center mb-6">
        <div className="text-3xl mb-3">✨</div>
        <h1
          className={`text-xl md:text-2xl font-light mb-2 ${dark ? 'text-rose-100' : 'text-gray-800'}`}
        >
          Nasze momenty, które noszę w sobie
        </h1>
        <p className={`text-xs ${dark ? 'text-rose-300/50' : 'text-gray-400'}`}>
          Klikaj w karty. To są małe okruszki mojego serca.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MEMORY_TILES.map((tile, idx) => (
          <button
            key={idx}
            onClick={() => setOpenIdx(openIdx === idx ? null : idx)}
            className={`rounded-2xl p-4 text-left transition-all duration-300 border-2 ${
              openIdx === idx
                ? dark
                  ? 'border-rose-500 bg-rose-950/40 col-span-2 md:col-span-2'
                  : 'border-rose-400 bg-rose-50 col-span-2 md:col-span-2'
                : dark
                  ? 'border-gray-800 hover:border-rose-700 bg-gray-800/40 hover:bg-gray-800/60'
                  : 'border-rose-100 hover:border-rose-300 bg-white/50 hover:bg-white/70'
            }`}
          >
            <p
              className={`text-sm font-medium mb-1 ${
                dark ? 'text-rose-200' : 'text-gray-700'
              }`}
            >
              {tile.title}
            </p>
            {openIdx === idx && (
              <p
                className={`text-xs font-light leading-relaxed whitespace-pre-line animate-v-fadeIn mt-2 ${
                  dark ? 'text-rose-300/80' : 'text-gray-600'
                }`}
              >
                {tile.content}
              </p>
            )}
          </button>
        ))}
      </div>

      <div className="text-center mt-4">
        <PrimaryButton onClick={onNext} dark={dark}>
          Otwórz list
        </PrimaryButton>
      </div>
    </CardShell>
  );
};

/* ════════════════════════════════════════════
   CARD 15 — LOVE LETTER
   ════════════════════════════════════════════ */

const CardLetter = ({
  dark,
  onNext,
}: {
  dark: boolean;
  onNext: () => void;
}): React.JSX.Element => (
  <CardShell dark={dark} wide>
    <div className="text-center mb-6">
      <div className="text-4xl mb-3">💌</div>
      <h1
        className={`text-2xl md:text-3xl font-light ${dark ? 'text-rose-100' : 'text-rose-800'}`}
      >
        Dla mojego Blondaska – Walentynki
      </h1>
    </div>

    <div
      className={`text-sm md:text-base font-light leading-relaxed whitespace-pre-line ${
        dark ? 'text-rose-200/85' : 'text-gray-700'
      }`}
    >
      {LOVE_LETTER}
    </div>

    <div className="text-center mt-8">
      <PrimaryButton onClick={onNext} dark={dark}>
        PS… kliknij, jeśli chcesz coś niegrzecznego 😈
      </PrimaryButton>
    </div>
  </CardShell>
);

/* ════════════════════════════════════════════
   CARD 16 — PS
   ════════════════════════════════════════════ */

const CardPS = ({ dark }: { dark: boolean }): React.JSX.Element => (
  <CardShell dark={dark}>
    <div className="text-center">
      <div className="text-4xl mb-5">PS. 😈</div>
      <div
        className={`text-sm md:text-base font-light leading-relaxed whitespace-pre-line text-left ${
          dark ? 'text-rose-200/85' : 'text-gray-700'
        }`}
      >
        {`Wiesz…
W momencie, kiedy pisałem fragment o posadzeniu Cię na blacie…
Przypomniał mi się nasz ostatni raz.
To cudowne uczucie bycia razem.
To napięcie między nami.
To był ten moment, po którym aż miałaś siniaki.

I powiem Ci…
Mam ochotę zobaczyć Ciebie
Twoje cudowne ciało
Śliczną bieliznę
Móc całować każdy centymetr Twojego ciała
Powoli, namiętnie
I poczuć, że jesteśmy jednością

Ale spokojnie…
mamy na to jeszcze czas 🩷`}
      </div>
    </div>
  </CardShell>
);

/* ════════════════════════════════════════════
   GLOBAL STYLES
   ════════════════════════════════════════════ */

const GlobalStyles = (): React.JSX.Element => (
  <style>{`
    @keyframes v-float {
      0%, 100% {
        transform: translateY(100vh) rotate(0deg);
        opacity: 0;
      }
      8% { opacity: 0.6; }
      92% { opacity: 0.6; }
      100% {
        transform: translateY(-100vh) rotate(360deg);
        opacity: 0;
      }
    }
    @keyframes v-fadeIn {
      from { opacity: 0; transform: translateY(14px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes v-shake {
      0%, 100% { transform: translateX(0); }
      20%  { transform: translateX(-8px); }
      40%  { transform: translateX(8px); }
      60%  { transform: translateX(-5px); }
      80%  { transform: translateX(5px); }
    }
    .animate-v-float  { animation: v-float  14s ease-in-out infinite; }
    .animate-v-fadeIn { animation: v-fadeIn 0.7s ease-out forwards; }
    .animate-v-shake  { animation: v-shake  0.5s ease-in-out; }

    /* smooth scrollbar inside cards */
    .overflow-y-auto::-webkit-scrollbar { width: 4px; }
    .overflow-y-auto::-webkit-scrollbar-track { background: transparent; }
    .overflow-y-auto::-webkit-scrollbar-thumb { background: rgba(244,114,182,0.3); border-radius: 99px; }
  `}</style>
);

/* ════════════════════════════════════════════
   MAIN ORCHESTRATOR
   ════════════════════════════════════════════ */

type CardId =
  | 'access'
  | 'access-ok'
  | 'intro'
  | 'quiz'
  | 'summary'
  | 'gallery'
  | 'letter'
  | 'ps';

const STATUS_TEXTS: Record<CardId, string> = {
  access: '',
  'access-ok': 'Blondasek verified ✅',
  intro: 'Blondasek verified ✅',
  quiz: 'Duma: rośnie 📈',
  summary: 'Duma: 100% 📈',
  gallery: 'Blondasek verified ✅',
  letter: '❤️',
  ps: '😈',
};

function heartPercent(card: CardId, quizProgress: number): number {
  switch (card) {
    case 'access':
      return 0;
    case 'access-ok':
      return 5;
    case 'intro':
      return 10;
    case 'quiz':
      return 10 + Math.round(quizProgress * 60);
    case 'summary':
      return 75;
    case 'gallery':
      return 85;
    case 'letter':
      return 95;
    case 'ps':
      return 100;
  }
}

export const ValentinesCardPage = (): React.JSX.Element => {
  const [card, setCard] = useState<CardId>('access');
  const [dark, setDark] = useState(false);
  const [quizProgress, setQuizProgress] = useState(1 / QUIZ.length);

  useEffect(() => {
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    setDark(prefersDark);
  }, []);

  const toggleDark = useCallback(() => setDark((d) => !d), []);

  const renderCard = (): React.JSX.Element => {
    switch (card) {
      case 'access':
        return (
          <CardAccess
            dark={dark}
            onNext={() => setCard('intro')}
          />
        );
      case 'intro':
        return <CardIntro dark={dark} onNext={() => setCard('quiz')} />;
      case 'quiz':
        return (
          <CardQuiz
            dark={dark}
            onProgress={setQuizProgress}
            onComplete={() => {
              setQuizProgress(1);
              setCard('summary');
            }}
          />
        );
      case 'summary':
        return <CardSummary dark={dark} onNext={() => setCard('gallery')} />;
      case 'gallery':
        return <CardGallery dark={dark} onNext={() => setCard('letter')} />;
      case 'letter':
        return <CardLetter dark={dark} onNext={() => setCard('ps')} />;
      case 'ps':
        return <CardPS dark={dark} />;
      default:
        return <CardAccess dark={dark} onNext={() => setCard('intro')} />;
    }
  };

  return (
    <>
      <GlobalStyles />
      <TopBar
        dark={dark}
        onToggleDark={toggleDark}
        heartPercent={heartPercent(card, quizProgress)}
        statusText={STATUS_TEXTS[card]}
      />
      {renderCard()}
    </>
  );
};
